import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Save } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { manageTrackerList, importTrackerExcel, fetchTrackerLists } from '../../../../redux/affiliateTrackerSlice';
import CommonDrawerLayout from '../../../../common_components/CommonDrawerLayout';
import AppButton from '../../../../common_components/AppButton';
import useToast from '../../../../common_components/Toast/useToast';
import AddInformationTab from './AddInformationTab';
import ImportPublisherTab from './ImportPublisherTab';
const CreatePublisherDrawer = ({ open, onClose, trackerId, clientId, onCreated }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const [activeTab, setActiveTab] = useState('add');
    const [saving, setSaving] = useState(false);
    // Add Information form state
    const [formData, setFormData] = useState({
        searchTerm: '', domain: '', articleVisitor: '', articleLink: '', rank: ''
    });
    // Import tab state
    const [importFile, setImportFile] = useState(null);
    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };
    const resetForm = () => {
        setFormData({ searchTerm: '', domain: '', articleVisitor: '', articleLink: '', rank: '' });
        setImportFile(null);
    };
    const handleClose = () => {
        resetForm();
        setActiveTab('add');
        onClose();
    };
    const handleSave = async () => {
        if (activeTab === 'add') {
            if (!formData.domain.trim()) {
                toast.error('Domain is required.');
                return;
            }
            try {
                setSaving(true);
                const payload = {
                    data: {
                        tracker_id: trackerId,
                        search_term: formData.searchTerm.trim(),
                        domain: formData.domain.trim(),
                        article_link: formData.articleLink.trim(),
                        article_visitor: Number(formData.articleVisitor) || 0,
                        rank: Number(formData.rank) || 0,
                        client_id: clientId || null,
                    }
                };
                const res = await dispatch(manageTrackerList({ action: 'create', payload })).unwrap();
                if (res?.tracker_id && onCreated) {
                    onCreated(res.tracker_id);
                }
                toast.success('Publisher created successfully!');
                dispatch(fetchTrackerLists());
                handleClose();
            }
            catch (err) {
                toast.error(err || 'Failed to create publisher.');
            }
            finally {
                setSaving(false);
            }
        }
        else {
            if (!importFile) {
                toast.error('Please select a file to import.');
                return;
            }
            try {
                setSaving(true);
                const fd = new FormData();
                fd.append('file', importFile);
                fd.append('tracker_id', trackerId);
                if (clientId)
                    fd.append('client_id', clientId);
                const res = await dispatch(importTrackerExcel(fd)).unwrap();
                if (res && res.length > 0 && res[0].tracker_id && onCreated) {
                    onCreated(res[0].tracker_id);
                }
                toast.success('Publishers imported successfully!');
                dispatch(fetchTrackerLists());
                handleClose();
            }
            catch (err) {
                toast.error(err || 'Failed to import publishers.');
            }
            finally {
                setSaving(false);
            }
        }
    };
    return (_jsx(CommonDrawerLayout, { open: open, onClose: handleClose, title: "Create Publisher", headerActions: _jsxs(AppButton, { variant: "contained", onClick: handleSave, disabled: saving, sx: { bgcolor: '#EE2A7B', color: '#fff', '&:hover': { bgcolor: '#D91F6A' } }, children: [_jsx(Save, { size: 15, style: { marginRight: 6 } }), saving ? 'Saving...' : 'Save'] }), width: "md", children: _jsxs(Box, { sx: { p: 2, display: 'flex', flexDirection: 'column', gap: 2.5, bgcolor: '#fff' }, children: [_jsx(Box, { sx: {
                        display: 'grid', gridTemplateColumns: '1fr 1fr',
                        bgcolor: '#F2F4F7', borderRadius: '8px', p: '4px', gap: '4px'
                    }, children: ['add', 'import'].map((tab) => (_jsx(Box, { onClick: () => setActiveTab(tab), sx: {
                            textAlign: 'center', py: '8px', borderRadius: '6px', cursor: 'pointer',
                            bgcolor: activeTab === tab ? '#EE2A7B' : 'transparent',
                            transition: 'all 0.2s',
                        }, children: _jsx(Typography, { sx: {
                                fontSize: '13px', fontWeight: 500,
                                color: activeTab === tab ? '#fff' : '#667085',
                            }, children: tab === 'add' ? 'Add Information' : 'Import Publisher' }) }, tab))) }), _jsx(Box, { sx: { bgcolor: '#fff', border: '1px solid #EAECF0', borderRadius: '8px', p: 2 }, children: activeTab === 'add' ? (_jsx(AddInformationTab, { searchTerm: formData.searchTerm, domain: formData.domain, articleVisitor: formData.articleVisitor, articleLink: formData.articleLink, rank: formData.rank, onChange: handleFieldChange })) : (_jsx(ImportPublisherTab, { file: importFile, onFileChange: setImportFile })) })] }) }));
};
export default CreatePublisherDrawer;
